import { ApiError } from '@/lib/api';
import {
  type MethodFieldRef,
  type MethodFunctionField,
  type MethodProblem,
  type MethodProblemKind,
  type MethodStyleField,
  maxOf,
  measureOf,
} from '@/lib/method-draft';
import {
  ANALYST_FUNCTIONS,
  ANALYST_FUNCTION_NAMES,
  type AnalystFunction,
  isNoyauCode,
  noyauOf,
} from '@matter_extractor/shared';
import { z } from 'zod';

/**
 * The words of Réglages > Méthode (analyste.md §4.4, issue #196; maquette
 * `Methode`), PURE and written once: the cards, the head and the tests read the
 * same sentences, and a second spelling would be a second meaning.
 *
 * Every text the planche draws is word for word here - the sentence under the
 * head, the two section titles and their pass, the labels of the fields, the
 * placeholder of the typical words, the footnote of the seed - with the apostrophes
 * of the product (typographic, as everywhere else on these screens).
 */

/** The sentence under the head: what each pass reads, and that a session freezes its version. */
export const METHOD_INTRO =
  'Ce que l’analyste sait de la méthode. La passe 1 lit les mots des trois fonctions pour donner leurs parts à chaque objectif ; la passe 2 lit les six styles pour comparer les candidats qui restent. Une session fige la version qu’elle joue : une retouche n’atteint jamais une session en cours.';

export const METHOD_FUNCTIONS_TITLE = 'Les trois fonctions';
export const METHOD_STYLES_TITLE = 'Les six styles';

/** The pill beside a section title: which pass reads it. */
export const METHOD_PASS_ONE = 'Passe 1';
export const METHOD_PASS_TWO = 'Passe 2';

/** The labels of the two fields of a function (§4.2). */
export const METHOD_FUNCTION_FIELD_LABEL: Readonly<Record<MethodFunctionField, string>> = {
  designates: 'Ce qu’elle désigne',
  words: 'Mots et expressions de la langue parlée',
};

/** The labels of the three fields of a style (§4.2). */
export const METHOD_STYLE_FIELD_LABEL: Readonly<Record<MethodStyleField, string>> = {
  name: 'Style',
  recognizes: 'Ce qui le reconnaît',
  typicalWords: 'Mots typiques',
};

/** What follows the label of the one facultative field. */
export const METHOD_OPTIONAL_MARK = '(facultatif)';

/** The placeholder of the typical words, which a style leaves empty at its seed. */
export const METHOD_TYPICAL_WORDS_PLACEHOLDER =
  'Les mots qui trahissent ce style, séparés par des virgules';

/** The tag of a field whose text is not the development version's. */
export const METHOD_TOUCHED_MARK = 'modifié';

/** The counter under a field: « 356 / 4000 caractères », on the characters the schema counts. */
export function characterCount(text: string, max: number): string {
  return `${measureOf(text)} / ${max} caractères`;
}

/**
 * Who signs the v1 the seed writes (`METHOD_SEED_AUTHOR` of the backend, which
 * this package cannot import): what makes the footnote of the planche true.
 */
export const METHOD_SEED_AUTHOR_NAME = 'graine #196';

/** The line at the foot of the tab while the development version is the seed's. */
export function seedFootnote(number: number): string {
  return `v${number} : graine de #196, tirée de la grille d’avant.`;
}

/* ------------------------------------------------------------- the refusals */

/** « « Ce qu’elle désigne » du Souverain », « « Style » du Souverain Magicien »: a field in a sentence. */
export function fieldPhrase(ref: MethodFieldRef): string {
  return ref.part === 'functions'
    ? `« ${METHOD_FUNCTION_FIELD_LABEL[ref.field]} » du ${ANALYST_FUNCTION_NAMES[ref.key]}`
    : `« ${METHOD_STYLE_FIELD_LABEL[ref.field]} » du ${noyauOf(ref.key).name}`;
}

/** Under a field: what keeps the version from being saved, in as few words as it takes. */
export function fieldRefusal(kind: MethodProblemKind, text: string, max: number): string {
  return kind === 'empty'
    ? 'À remplir pour enregistrer une nouvelle version.'
    : `${measureOf(text)} caractères : ${max} au plus.`;
}

/** One problem in the sentence of the summary. */
function problemClause(problem: MethodProblem): string {
  return problem.kind === 'empty'
    ? `${fieldPhrase(problem.ref)} est vide`
    : `${fieldPhrase(problem.ref)} dépasse ${maxOf(problem.ref)} caractères`;
}

/** How many problems the summary names before it counts the rest. */
const SUMMARY_SHOWN = 3;

/**
 * The summary above the cards while the draft cannot become a version: the
 * button of the head only knows « rien n'a changé », so this is the sentence
 * that says WHICH fields to mend, the first three named and the rest counted.
 */
export function blockedSentence(problems: readonly MethodProblem[]): string {
  const named = problems.slice(0, SUMMARY_SHOWN).map(problemClause);
  const rest = problems.length - named.length;
  const list =
    rest > 0
      ? `${named.join(' ; ')} ; et ${rest === 1 ? '1 autre' : `${rest} autres`}`
      : named.join(' ; ');
  return `Cette version ne peut pas encore être enregistrée : ${list}.`;
}

/* ----------------------------------------------- the refusal of the server */

/**
 * The body a 400 of the save arrives as: the validator of the route answers
 * `{ success: false, error: ZodError }`, and `ApiError.details` keeps the body
 * whole when it has no `details` key (`lib/api.ts`).
 */
const validatorBodySchema = z.object({
  error: z.object({
    issues: z
      .array(z.object({ code: z.string(), path: z.array(z.union([z.string(), z.number()])) }))
      .min(1),
  }),
});

function isFunctionKey(value: unknown): value is AnalystFunction {
  return typeof value === 'string' && (ANALYST_FUNCTIONS as readonly string[]).includes(value);
}

/** The field a path of the schema names, whether or not it starts at `content`. */
function fieldOfPath(path: readonly (string | number)[]): MethodFieldRef | null {
  const rest = path[0] === 'content' ? path.slice(1) : path;
  const [part, key, field] = rest;
  if (rest.length !== 3 || typeof key !== 'string') return null;
  if (part === 'functions' && isFunctionKey(key) && (field === 'designates' || field === 'words')) {
    return { part, key, field };
  }
  if (
    part === 'styles' &&
    isNoyauCode(key) &&
    (field === 'name' || field === 'recognizes' || field === 'typicalWords')
  ) {
    return { part, key, field };
  }
  return null;
}

/**
 * The refusal of « Enregistrer une nouvelle version » as ONE sentence, or `null`
 * when the failure is not one this screen words (the layer then says its own).
 *
 * The button of the head is closed on exactly what the schema refuses, so a 400
 * of the save is a race - a bound moved between the two - or a field the screen
 * cannot name. A refusal the screen CAN name is said in the words of the card:
 * the field, then why, and that nothing was written. One it cannot is left to the
 * layer, which never prints the server's English.
 */
export function methodRefusalSentence(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.status !== 400) return null;
  const body = validatorBodySchema.safeParse(error.details);
  if (!body.success) return null;

  const clauses: string[] = [];
  for (const issue of body.data.error.issues) {
    const ref = fieldOfPath(issue.path);
    if (ref === null) return null;
    if (issue.code === 'too_small') clauses.push(`${fieldPhrase(ref)} est vide`);
    else if (issue.code === 'too_big') {
      clauses.push(`${fieldPhrase(ref)} dépasse ${maxOf(ref)} caractères`);
    } else return null;
  }
  return `Cette version ne tient pas : ${clauses.join(' ; ')}. Rien n’a été écrit.`;
}

/* --------------------------------------------------- Méthode not on this server */

/**
 * What the tab says when the server answers 404: a vitrine that has not received
 * Méthode yet (it arrives by « Passer en production », vitrine.md §5, K12), and
 * - on an atelier, where it is born at boot - a store the seed has not reached.
 */
export const METHOD_NOT_BORN_VITRINE =
  'Méthode n’est pas encore arrivée sur la vitrine : elle y arrive par « Passer en production », depuis form.dev.';
export const METHOD_NOT_BORN_ATELIER =
  'Méthode n’a pas encore de version sur ce serveur : elle naît d’une graine au démarrage. Redémarrez le serveur pour la faire naître.';

/** The sentence of a failed read that is not a 404. */
export const METHOD_READ_FAILED = 'Méthode n’a pas pu être lue. Rechargez l’écran.';
